import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import QuestionCategoryFilter from '@app/src/pages/ResourceCollection/Filters/QuestionCategoryFilter'
import QuestionnaireFilter from '@app/src/pages/ResourceCollection/Filters/QuestionnaireFilter'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { useQuestionCategories } from '@app/src/hooks/useQuestionCategories'
import { Box, Button, Grid } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { FacetItem, FiltersProps } from '..'
import AllFiltersButton from './AllFiltersButton'
import TextFieldFilter from './TextFieldFilter'

const getCustomSort = (questionCategory: { customSort?: (a: string, b: string) => number }) => {
  const customSort = questionCategory.customSort
  if (!customSort) return undefined
  return (a: FacetItem, b: FacetItem) => customSort(a.label, b.label)
}

interface StandardQuestionFiltersProps extends FiltersProps {
  showQuestionnaireFilter?: boolean
}

const StandardQuestionFilters = ({
  allowedFilters,
  implicitFilters,
  resetPage,
  showQuestionnaireFilter,
}: StandardQuestionFiltersProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { basicCategories, basicAndRegularCategories, esrsCategories } = useQuestionCategories()

  return (
    <Grid container spacing={1} rowSpacing={2}>
      <Filters resetPage={resetPage} allowedFilters={allowedFilters}>
        {({ filters, clearQueryFilter, inDrawerFiltersCount }) => {
          const isFiltered = Object.keys(filters).length > 0
          return (
            <>
              <Grid item sm={10} md={8} lg={9} xl={10}>
                <Grid container columnSpacing={1} rowSpacing={2}>
                  <Grid item sm={12} md={2.5} lg={2} xl={1.5}>
                    <Filter operator={Operators.EqualTo} name="id">
                      {({ filterName, value, operator }) => (
                        <TextFieldFilter
                          fullWidth
                          size="small"
                          defaultValue={value}
                          label={formatMessage({ id: 'general.id' })}
                          filterName={filterName}
                          operator={operator}
                          name="id"
                        />
                      )}
                    </Filter>
                  </Grid>
                  <Grid item sm={12} md={9.5} lg={10} xl={4.5}>
                    <Filter operator={Operators.Contains} name="questionText">
                      {({ filterName, value, operator }) => (
                        <TextFieldFilter
                          fullWidth
                          size="small"
                          defaultValue={value}
                          label={formatMessage({ id: 'schemas.question.name' })}
                          filterName={filterName}
                          operator={operator}
                          name="questionName"
                        />
                      )}
                    </Filter>
                  </Grid>
                  {basicCategories.map(cf => (
                    <Grid item key={cf.name} sm={12} md={12} lg={4} xl={2}>
                      <QuestionCategoryFilter
                        categoryId={cf.id}
                        categoryName={cf.name}
                        implicitFilters={implicitFilters}
                        customSort={getCustomSort(cf)}
                      />
                    </Grid>
                  ))}
                  {showQuestionnaireFilter && (
                    <Grid item sm={12} md={12} lg={4} xl={3}>
                      <QuestionnaireFilter implicitFilters={implicitFilters} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item sm={8} md={4} lg={3} xl={2}>
                <Box display="inline-block">
                  <AllFiltersButton
                    inDrawerFiltersCount={inDrawerFiltersCount}
                    openDrawer={() =>
                      openDrawer(
                        <DrawerViewFilters allowedFilters={allowedFilters}>
                          <Box px={2}>
                            <FilterSection label={formatMessage({ id: 'configurations.sustainabilityLibrary' })}>
                              {basicAndRegularCategories.map(cf => (
                                <QuestionCategoryFilter
                                  key={cf.name}
                                  categoryId={cf.id}
                                  categoryName={cf.name}
                                  implicitFilters={implicitFilters}
                                  customSort={getCustomSort(cf)}
                                />
                              ))}
                            </FilterSection>
                            <FilterSection label={formatMessage({ id: 'schemas.question.esrs' })}>
                              {esrsCategories.map(cf => (
                                <QuestionCategoryFilter
                                  key={cf.name}
                                  categoryId={cf.id}
                                  categoryName={cf.name}
                                  implicitFilters={implicitFilters}
                                  customSort={getCustomSort(cf)}
                                />
                              ))}
                            </FilterSection>
                          </Box>
                        </DrawerViewFilters>,
                        { showAboveDialogs: true },
                      )
                    }
                  />
                </Box>

                {isFiltered && (
                  <Button variant="text" onClick={clearQueryFilter} sx={{ ml: 1 }}>
                    {formatMessage({ id: 'filters.clearAll' })}
                  </Button>
                )}
              </Grid>
            </>
          )
        }}
      </Filters>
    </Grid>
  )
}

export default StandardQuestionFilters
